import { useEffect, useRef, useState } from 'react';
import type { QuoteHeaderProps } from './types';
import { Badge } from './ui/primitives';

/** The subtitle's own height, and the gap above it. */
const SUBTITLE_MAX_HEIGHT = 19.2;
const SUBTITLE_MARGIN = 3.2;
/** What collapsing it takes off the header. */
const SUBTITLE_HEIGHT = SUBTITLE_MAX_HEIGHT + SUBTITLE_MARGIN;

/**
 * How far the sentinel must pass the scroller's top edge before the subtitle
 * collapses, and how far back before it returns.
 *
 * Collapsing shortens the page, and at scroll positions near the scroller's
 * limit the browser clamps scrollTop to compensate — which drags the sentinel
 * back across a single boundary, expanding the subtitle, lengthening the page,
 * and starting again. That feedback loop is the flicker.
 *
 * A deadband wider than the height the collapse removes cannot be crossed by its
 * own effect, so the state settles. That is why STICK_AT is derived from
 * SUBTITLE_HEIGHT rather than picked: the invariant holds even if the subtitle
 * changes size.
 */
const STICK_AT = SUBTITLE_HEIGHT + 10;
const RELEASE_AT = 0;

/** The scrolling ancestor, or null when the page itself scrolls. */
function scrollParentOf(el: HTMLElement): HTMLElement | null {
	let node = el.parentElement;
	while (node) {
		const { overflowY } = getComputedStyle(node);
		if (overflowY === 'auto' || overflowY === 'scroll') return node;
		node = node.parentElement;
	}
	return null;
}

/**
 * The quote's title bar.
 *
 * Sticks to the top of the scroller and drops its subtitle as it does, so a page
 * this long keeps its identity and its two primary actions on screen without
 * spending three lines on it. The sentinel above decides when.
 *
 * Measured against the scrolling ancestor rather than the viewport: this page
 * scrolls inside the app's layout container, so a viewport-rooted
 * IntersectionObserver watches the wrong edge. Reading positions directly also
 * buys the hysteresis above, which one observer threshold cannot express.
 *
 * ── What it publishes ──
 *
 * Anything else on the page that sticks has to clear this bar, and its height is
 * not a constant: the subtitle collapses, the row wraps on a narrow window, and
 * the actions are the caller's. Every sticky offset was a hardcoded 72px guessed
 * from one of those states, so in the others a pinned element sat behind the
 * header. It measures itself instead and publishes
 *
 *   --quote-header-h    the bar's height
 *   --quote-scrollport-h  how tall the scrolling area is
 *
 * on the scroller (or the document, when the page itself scrolls), for anything
 * inside to offset from. Both carry fallbacks at every use, so a caller rendered
 * without this header still lands somewhere sane.
 */
export function QuoteHeader({ name, status, subtitle, actions }: QuoteHeaderProps) {
	const sentinel = useRef<HTMLDivElement>(null);
	const bar = useRef<HTMLDivElement>(null);
	const [stuck, setStuck] = useState(false);

	/* Measured, not derived from `stuck`: the collapse is one of several things
	   that change this height, and the others (wrapping, the caller's actions)
	   have no state here to read. */
	useEffect(() => {
		const el = bar.current;
		if (!el) return;
		const scroller = scrollParentOf(el);
		const host = scroller ?? document.documentElement;

		const publish = () => {
			host.style.setProperty('--quote-header-h', `${Math.round(el.offsetHeight)}px`);
			host.style.setProperty(
				'--quote-scrollport-h',
				`${Math.round(scroller ? scroller.clientHeight : window.innerHeight)}px`
			);
		};

		publish();
		const observer = new ResizeObserver(publish);
		observer.observe(el);
		if (scroller) observer.observe(scroller);
		window.addEventListener('resize', publish);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', publish);
			host.style.removeProperty('--quote-header-h');
			host.style.removeProperty('--quote-scrollport-h');
		};
	}, []);

	useEffect(() => {
		const el = sentinel.current;
		if (!el) return;
		const scroller = scrollParentOf(el);
		let frame = 0;

		const measure = () => {
			frame = 0;
			const rootTop = scroller ? scroller.getBoundingClientRect().top : 0;
			// How far the sentinel has travelled past the top of the scroller.
			const passed = rootTop - el.getBoundingClientRect().top;
			setStuck((prev) => (prev ? passed > RELEASE_AT : passed > STICK_AT));
		};
		// Coalesced to one read per frame: scroll fires far more often than the
		// header can meaningfully change, and each read forces layout.
		const onScroll = () => {
			if (!frame) frame = requestAnimationFrame(measure);
		};

		measure();
		const target: HTMLElement | Window = scroller ?? window;
		target.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		return () => {
			if (frame) cancelAnimationFrame(frame);
			target.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, []);

	return (
		<>
			<div ref={sentinel} className="h-4" />
			<div
				ref={bar}
				className="sticky top-0 z-20 mb-2 flex flex-wrap items-center gap-x-4 gap-y-2.5 border-b border-line-1 bg-canvas px-0 pt-2.5 pb-3"
			>
				<div className="min-w-[240px] flex-1">
					<div className="flex flex-wrap items-center gap-2.5">
						<h1 className="m-0 text-3xl leading-[1.15] font-medium -tracking-[0.02em] text-text-1">
							{name}
						</h1>
						<Badge tone={status.tone} dot>
							{status.label}
						</Badge>
					</div>
					{subtitle && (
						<div
							className="overflow-hidden truncate text-base text-text-3 transition-all duration-150 ease-[var(--ease-out)]"
							style={{
								maxHeight: stuck ? 0 : SUBTITLE_MAX_HEIGHT,
								opacity: stuck ? 0 : 1,
								marginTop: stuck ? 0 : SUBTITLE_MARGIN,
							}}
						>
							{subtitle}
						</div>
					)}
				</div>
				{actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
			</div>
		</>
	);
}
